import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
  OLT,
  OLTBase,
  OLTPagingRes,
  OLTsTroncalBase,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { isAxiosError } from 'axios';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchOLTs = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetOLTsParams> = {}) => {
  return useQuery({
    queryKey: ['olts-red', ...Object.values(params ?? {})],
    queryFn: () => getOLTs(params),
    enabled: enabled,
  });
};

export const useGetOLT = (id: string) => {
  return useQuery({
    queryKey: ['olt-red', id],
    queryFn: () => getOLT(id),
    gcTime: 0
  });
};

export const useCreateOLT = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createOLT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['olts-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'OLT creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateOLT = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateOLT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['olts-red'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'OLT actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteOLT = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOLT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['olts-red'] });
    },
    onError: () => { },
  });
};

///* axios
export type GetOLTsParams = Partial<OLT> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateOLTParams = Omit<OLT, 'id_olt'>;
export type UpdateOLTParams = {
  id: string;
  data: CreateOLTParams;
};

export const getOLTs = async (params?: GetOLTsParams) => {
  const stateParams = { ...params };

  // // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<OLTPagingRes>(`${urlBackend}api/v1/olt/?${queryParams}`, true);
};

export const getOLT = async (id: string) => {
  try {
    return await get<OLT>(`${urlBackend}api/v1/olt/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createOLT = async (data: CreateOLTParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<OLT>(`${urlBackend}api/v1/olt/`, data, true);
};

export const updateOLT = async ({ id, data }: UpdateOLTParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<OLT>(`${urlBackend}api/v1/olt/${id}/`, data, true);
};

export const deleteOLT = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<OLT>(`${urlBackend}api/v1/olt/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};

//CUSTOMS
/***
 * Importar Excel OLT
 * 
*/
export const useFetchOLTsImport = () => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: createImportOLTsFromExcel,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['olts-create-array'] });
      setAlert(true, data.message, data.status);
    },

    onError: (error => {
      if (isAxiosError(error)) {
        const respAxiosData = error.response?.data || {};
        if (respAxiosData.code == 400)
          return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
      }
      handleAxiosError(error);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);

    }
  });
};
export const createImportOLTsFromExcel = async (data: { olts: any[] }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const res = await post<OLTBase>(`${urlBackend}api/v1/importar-olts/`, data, true);
  return res;
};


/***
 * Importar Excel OLT Troncales
 * 
*/
export const useFetchOLTsTroncalesImport = () => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: createImportOLTsTroncalesFromExcel,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['oltsTroncales-create-array'] });
      setAlert(true, data.message, data.status);
    },

    onError: (error => {
      if (isAxiosError(error)) {
        const respAxiosData = error.response?.data || {};
        if (respAxiosData.code == 400)
          return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
      }
      handleAxiosError(error);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);

    }
  });
};
export const createImportOLTsTroncalesFromExcel = async (data: { oltsTroncales: any[] }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const { oltsTroncales } = data
  const res = await post<OLTsTroncalBase>(`${urlBackend}api/v1/asociar-nap-con-olt/`, { olts: oltsTroncales }, true);
  return res;
};




/***
 * Generar Mangas troncales desde OLT
 */
export const useGenerarMangasTroncalesDesdeOLT = ({
  customMessageErrorAlert,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: generateMangasTroncalesDesdeOLT,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['olt-red', variables.id] });
      setAlert(
        true,
        customSuccessMessage ?? 'Mangas troncales generadas correctamente',
        AlertSeverityEnum.success
      );
    },
    onError: error => {
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export type TroncalesDesdeOLTParams = {
  id: string;
  data: {
    generar_mangas?:boolean;
    rango_inicio_manga?:string
    rango_fin_manga?:string
    observaciones?: any
  };
};
export const generateMangasTroncalesDesdeOLT = async ({ id, data }: TroncalesDesdeOLTParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<OLT>(`${urlBackend}api/v1/olt/${id}/`, data, true);
};
